import { useSnackbar } from 'notistack';
import { useApi } from '../../shared/context';
import { ProductSubscription, ProductSubscriptionSuccessFeeTier } from '../../shared/models';
import { useMutation, useQueryClient } from 'react-query';
import { Dialog, DialogContent, DialogTitle, Grid } from '@mui/material';
import {
	CerebroForm,
	CerebroLabel,
	CerebroMaskedInput,
	CerebroPrimaryButton,
	CerebroSecondaryButton,
} from '../../shared/components';
import { Field, Formik, FormikErrors } from 'formik';
import { FormikUtils, NumberUtils } from '../../shared/utils';
import { CerebroApiEndpoints } from '../../shared/utils/api';
import { QueryKeys } from '../../shared/hooks';
import { EmptyGuid } from '../../shared/constants';
import { MaxCurrencyLength } from '../../shared/utils/platform';
import { getApiResponseData } from '../../shared/utils/axios';

interface SuccessFeeTierFormDialogProps {
	dealGuid: string | undefined;
	subscription: ProductSubscription | null;
	tier: ProductSubscriptionSuccessFeeTier | null;
	close: () => void;
}

export const SuccessFeeTierFormDialog: React.FC<SuccessFeeTierFormDialogProps> = ({
	dealGuid,
	subscription,
	tier,
	close,
}) => {
	const api = useApi();
	const snackbar = useSnackbar();
	const client = useQueryClient();

	const tierMutation = useMutation(
		async ({ values }: SuccessFeeTierFormMutationVariables) => {
			const patchValues = {
				basisPoints: NumberUtils.ParseDecimal(values.basisPoints, undefined),
				lowerFundingAmount: NumberUtils.ParseDecimal(values.lowerFundingAmount, undefined),
				upperFundingAmount: NumberUtils.ParseDecimal(values.upperFundingAmount, undefined),
			};

			const { data, error } = await getApiResponseData<ProductSubscription>(
				tier?.guid == EmptyGuid
					? api.post(CerebroApiEndpoints.Deals.SuccessFeeTiers(dealGuid, subscription?.guid), patchValues)
					: api.patch(
							CerebroApiEndpoints.Deals.SepcificSuccessFeeTiers(dealGuid, subscription?.guid, tier?.guid),
							patchValues
					  )
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: subscription => {
				client.invalidateQueries(QueryKeys.Deal.UpdateStatus(dealGuid));
				client.invalidateQueries(QueryKeys.Deal.SubscriptionTiers(dealGuid, subscription?.guid));
				snackbar.enqueueSnackbar('Deal subscription success fee tier was successfully saved.', {
					variant: 'success',
				});
				close();
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
				close();
			},
		}
	);

	var validateForm = (values: SuccessFeeTierFormValues): SuccessFeeTierFormErrors => {
		const errors: SuccessFeeTierFormErrors = {};

		return errors;
	};

	return (
		<Dialog open={tier !== null} onClose={close}>
			<DialogTitle>Success Fee Tier</DialogTitle>
			<DialogContent>
				<Formik
					initialValues={{
						basisPoints: NumberUtils.FormatDecimal(tier?.basisPoints ?? 0),
						lowerFundingAmount: NumberUtils.FormatDecimal(tier?.lowerFundingAmount),
						upperFundingAmount: NumberUtils.FormatDecimal(tier?.upperFundingAmount),
					}}
					validate={validateForm}
					onSubmit={FormikUtils.MutateOnSubmit(tierMutation.mutate)}
				>
					{({ values, isSubmitting, errors, touched, handleSubmit, setFieldValue }) => (
						<CerebroForm onSubmit={handleSubmit}>
							<Grid item xs={11}>
								<CerebroLabel required id="lowerFundingAmount">
									Lower Funding Amount
								</CerebroLabel>
								<Field
									formatas="currency"
									as={CerebroMaskedInput}
									maxLength={MaxCurrencyLength}
									id="lowerFundingAmount"
									name="lowerFundingAmount"
									disabled={isSubmitting}
								/>
							</Grid>
							<Grid item xs={11}>
								<CerebroLabel id="upperFundingAmount">Upper Funding Amount</CerebroLabel>
								<Field
									formatas="currency"
									as={CerebroMaskedInput}
									maxLength={MaxCurrencyLength}
									id="upperFundingAmount"
									name="upperFundingAmount"
									disabled={isSubmitting}
								/>
							</Grid>
							<Grid item xs={11}>
								<CerebroLabel required id="basisPoints">
									Basis Points
								</CerebroLabel>
								<Field
									as={CerebroMaskedInput}
									id="basisPoints"
									name="basisPoints"
									disabled={isSubmitting}
								/>
							</Grid>

							<CerebroPrimaryButton type="submit" loading={isSubmitting} sx={{ marginTop: 6 }}>
								Save
							</CerebroPrimaryButton>
							<CerebroSecondaryButton sx={{ marginTop: 6, marginLeft: 1 }} onClick={() => close()}>
								Cancel
							</CerebroSecondaryButton>
						</CerebroForm>
					)}
				</Formik>
			</DialogContent>
		</Dialog>
	);
};

type SuccessFeeTierFormErrors = FormikErrors<SuccessFeeTierFormValues>;
type SuccessFeeTierFormMutationVariables = FormikUtils.MutateOnSubmitVariables<SuccessFeeTierFormValues>;
interface SuccessFeeTierFormValues {
	basisPoints: string;
	lowerFundingAmount: string;
	upperFundingAmount: string;
}
